import { Component, computed, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import type { CallLogItem } from '../../home.models';
import type { MeetingCallMode } from '../../../../../store/meeting/meeting.models';

@Component({
  selector: 'app-calls-screen',
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './calls-screen.component.html',
  styleUrl: './calls-screen.component.scss'
})
export class CallsScreenComponent {
  readonly logs = input<CallLogItem[]>([]);
  readonly startCallRequest = output<{ channelId: string; mode: MeetingCallMode }>();
  protected readonly activeFilter = signal<'all' | 'missed'>('all');
  protected readonly searchTerm = signal('');

  protected readonly filteredLogs = computed(() => {
    const filter = this.activeFilter();
    const query = this.searchTerm().trim().toLowerCase();

    return this.logs().filter((entry) => {
      const passFilter = filter === 'all' || entry.status === 'missed' || entry.status === 'declined';
      if (!passFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      return entry.name.toLowerCase().includes(query);
    });
  });

  protected readonly missedCount = computed(() =>
    this.logs().filter((entry) => entry.status === 'missed' || entry.status === 'declined').length
  );

  protected readonly weeklySummary = computed(() => {
    const logs = this.logs();
    return {
      total: logs.length,
      completed: logs.filter((entry) => entry.status === 'completed').length,
      missed: logs.filter((entry) => entry.status === 'missed').length,
      declined: logs.filter((entry) => entry.status === 'declined').length,
    };
  });

  protected setFilter(filter: 'all' | 'missed'): void {
    this.activeFilter.set(filter);
  }

  protected onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  protected getDirectionIcon(direction: CallLogItem['direction'], status: CallLogItem['status']): string {
    if (status === 'missed' || status === 'declined') {
      return 'call_missed';
    }
    if (direction === 'incoming') {
      return 'call_received';
    }
    return 'call_made';
  }

  protected getDirectionLabel(direction: CallLogItem['direction']): string {
    if (direction === 'incoming') {
      return 'Incoming';
    }
    return 'Outgoing';
  }

  protected getStatusLabel(status: CallLogItem['status']): string {
    if (status === 'completed') {
      return 'Completed';
    }
    if (status === 'declined') {
      return 'Declined';
    }
    if (status === 'ringing') {
      return 'Ringing';
    }
    return 'Missed';
  }

  protected onStartCall(channelId: string, mode: MeetingCallMode): void {
    if (!channelId) {
      return;
    }

    this.startCallRequest.emit({ channelId, mode });
  }
}
